import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { FormikErrors, FormikState, useFormik } from 'formik'

import {
  DeliveryForecastDespatchAdvicePackagingBatch,
  DeliveryForecastDespatchAdvicePackagingItem,
  getPackagingItemHierarchyRestrictions,
} from '../../../../../../../data/models'
import {
  FieldToValidate,
  validateFormikFields,
} from '../../../../../../../utils/helper/validateFormikFields'
import { useConfigContext } from '../../../../../../ui-config'
import { getParentOptions } from '../../../../../utils'
import { FormFieldName, FormValues } from '../../common'
import { EditView } from './Edit.view'

type Props = {
  handleSubmit: (
    formData: FormValues,
    resetForm: (
      nextState?: Partial<FormikState<FormValues>> | undefined,
    ) => void,
  ) => Promise<void>
  isRequestRunning: boolean
  isVisible: boolean
  setIsVisible: React.Dispatch<React.SetStateAction<boolean>>
  despatchAdviceId: string
  itemId: string
  data?: DeliveryForecastDespatchAdvicePackagingItem
  packagingsItems: DeliveryForecastDespatchAdvicePackagingItem[] | undefined
  initialValues: FormValues
}

export const EditForm: React.FC<Props> = ({
  handleSubmit,
  isRequestRunning,
  isVisible,
  setIsVisible,
  despatchAdviceId,
  itemId,
  data,
  packagingsItems,
  initialValues,
}) => {
  const { t } = useTranslation()
  const { uiConfig: config } = useConfigContext()

  const formik = useFormik<FormValues>({
    enableReinitialize: true,
    initialValues: initialValues,
    validate: formData => {
      let errors: FormikErrors<FormValues> = {}

      const isMaster = formData.label === 'Master'
      const isSingle = formData.label === 'Single'
      const isHelper = formData.label === 'Helper'

      const fieldsToValidate: FieldToValidate[] = []

      fieldsToValidate.push({
        fieldName: 'label',
        config: config.deliveryForecastDespatchAdvice.editPackagingItem.label,
      })

      if (isMaster || isSingle) {
        fieldsToValidate.push(
          {
            fieldName: 'huNumber',
            config:
              config.deliveryForecastDespatchAdvice.editPackagingItem.huNumber,
          },
          {
            fieldName: 'isOuterPackaging',
            config:
              config.deliveryForecastDespatchAdvice.editPackagingItem
                .isOuterPackaging,
          },
        )
      }

      if (isSingle) {
        fieldsToValidate.push(
          {
            fieldName: 'quantity',
            config:
              config.deliveryForecastDespatchAdvice.editPackagingItem.quantity,
          },
          {
            fieldName: 'quantityUnit',
            config:
              config.deliveryForecastDespatchAdvice.editPackagingItem
                .quantityUnit,
          },

          {
            fieldName: 'weight',
            config:
              config.deliveryForecastDespatchAdvice.editPackagingItem.weight,
          },
          {
            fieldName: 'weightUnit',
            config:
              config.deliveryForecastDespatchAdvice.editPackagingItem
                .weightUnit,
          },
        )
      }

      if (isSingle || isHelper) {
        fieldsToValidate.push({
          fieldName: 'selectMaster',
          config:
            config.deliveryForecastDespatchAdvice.editPackagingItem
              .selectMaster,
        })
      }

      validateFormikFields(fieldsToValidate, formData, errors)

      const { isParentMandatory } = getPackagingItemHierarchyRestrictions(
        formData.label,
      )
      if (isParentMandatory && !formData.selectMaster) {
        errors.selectMaster = t('common.errors.selectionRequired')
      }

      if ((formData.batches || []).length > 0) {
        errors.batches = []

        formData.batches?.forEach((data, index) => {
          const batchErrors: FormikErrors<DeliveryForecastDespatchAdvicePackagingBatch> =
            {}

          const fieldsToValidate: FieldToValidate[] = [
            {
              fieldName: 'Batchnumber',
              config:
                config.deliveryForecastDespatchAdvice.addConfirmationBatch
                  .batchnumber,
            },
            {
              fieldName: 'Quantity',
              config:
                config.deliveryForecastDespatchAdvice.addConfirmationBatch
                  .quantity,
            },
          ]

          validateFormikFields(fieldsToValidate, data, batchErrors)

          if (!data.Batchnumber) {
            batchErrors.Batchnumber = t('common.errors.required')
          }

          if (data.Quantity == null) {
            batchErrors.Quantity = t('common.errors.required')
          } else if (data.Quantity < 1) {
            batchErrors.Quantity = t('common.errors.numberTooSmall', {
              number: 0,
            })
          } else if (data.Quantity > (data.OpenQuantity || 0)) {
            batchErrors.Quantity = t('common.errors.numberTooBig', {
              number: data.OpenQuantity || 0,
            })
          }

          // Only push to errors.batches if there are any errors
          if (Object.keys(batchErrors).length > 0) {
            // @ts-ignore
            errors.batches[index] = batchErrors
          }
        })

        if (errors.batches.length === 0) {
          delete errors.batches
        }
      }

      if (!formData.packagingData) {
        errors.packagingData = t(
          'deliveryForecastDespatchAdvice.shippingDetails.packagingTable.add.dialog.packagingMaterial.errorMessage',
        )
      }

      return errors
    },
    onSubmit: async formData => {
      await handleSubmit(formData, formik.resetForm)
    },
  })

  const isFormFieldValid = (name: FormFieldName, index?: number) => {
    if (index !== undefined) {
      return !(
        formik.touched.batches?.[index]?.[
          name as keyof DeliveryForecastDespatchAdvicePackagingBatch
        ] &&
        (
          formik.errors.batches?.[
            index
          ] as FormikErrors<DeliveryForecastDespatchAdvicePackagingBatch>
        )?.[name as keyof DeliveryForecastDespatchAdvicePackagingBatch]
      )
    }

    return !(
      formik.touched[name as keyof FormValues] &&
      formik.errors[name as keyof FormValues]
    )
  }

  const getFormErrorMessage = (name: FormFieldName, index?: number) => {
    if (index !== undefined) {
      const fieldErrors = formik.errors.batches?.[
        index
      ] as FormikErrors<DeliveryForecastDespatchAdvicePackagingBatch>
      return (
        !isFormFieldValid(name, index) && (
          <small className="p-error">
            {
              fieldErrors?.[
                name as keyof DeliveryForecastDespatchAdvicePackagingBatch
              ]
            }
          </small>
        )
      )
    }

    return (
      !isFormFieldValid(name) && (
        <small className="p-error">
          {formik.errors[name as keyof FormValues] as string}
        </small>
      )
    )
  }

  const handleClose = () => {
    formik.resetForm()
    setIsVisible(false)
  }

  const parents = useMemo(
    () => getParentOptions(packagingsItems!, formik.values.label),
    [packagingsItems, formik.values.label],
  )

  return (
    <EditView
      visible={isVisible}
      setVisible={setIsVisible}
      formik={formik}
      isFormFieldValid={isFormFieldValid}
      getFormErrorMessage={getFormErrorMessage}
      handleClose={handleClose}
      isRequestRunning={isRequestRunning}
      parents={parents}
      despatchAdviceId={despatchAdviceId}
      itemId={itemId}
      data={data}
    />
  )
}
